import React, { Suspense, lazy } from "react";

// Keep this Import compatibility package at the top
import "@ant-design/v5-patch-for-react-19";

import "./styles/fonts.css";
import { useSelector } from "react-redux";
import { Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, App as AntApp, Spin } from "antd";
import { ErrorBoundary } from "react-error-boundary";
import { useAuth } from "./hooks/useAuth";
import { selectThemeFromAuth } from "./store/slices/authSlice";
import { useNotifications } from "./hooks/useNotifications";
import { useSessionGuard } from "./hooks/useSessionGuard";
import { selectAppliedTheme } from "./store/slices/themeSlice";

// Lazy load components
const QRScanner = lazy(() => import("./components/auth/QRScanner"));
const ScanHandler = lazy(() => import("./pages/ScanHandler"));
const MainLayout = lazy(() => import("./components/layout/MainLayout"));
const Home = lazy(() => import("./pages/Home"));
const FnBMenu = lazy(() => import("./pages/FnBMenu"));
const Housekeeping = lazy(() => import("./pages/Housekeeping"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const FrontDesk = lazy(() => import("./pages/FrontDesk"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const MyRequests = lazy(() => import("./pages/MyRequests"));
const NotificationSettings = lazy(() => import("./components/NotificationSettings"));
const Folio = lazy(() => import("./pages/Folio"));
const HotelInfo = lazy(() => import("./pages/HotelInfo"));
const NotificationPrompt = lazy(() => import("./components/NotificationPrompt"));
const GlobalFeedbackModal = lazy(() => import("./components/common/GlobalFeedbackModal"));

// Protected route component
const ProtectedRoute = ({ children }) => {
  const isAuth = useAuth();
  if (!isAuth) {
    return <Navigate to="/scan" replace />;
  }
  return children;
};

// Default theme configuration (fallback)
const defaultTheme = {
  primaryColor: '#ff670a',
  logoUrl: null,
  fontStyle: 'Inter, system-ui, -apple-system, sans-serif',
};

// App Router
const AppRouter = () => (
  <Routes>
    <Route path="/scan" element={<QRScanner />} />
    <Route path="/guest/scan" element={<ScanHandler />} />
    <Route
      path="/"
      element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="/home" replace />} />
      <Route path="home" element={<Home />} />
      <Route path="fnb" element={<FnBMenu />} />
      <Route path="housekeeping" element={<Housekeeping />} />
      <Route path="maintenance" element={<Maintenance />} />
      <Route path="front-desk" element={<FrontDesk />} />
      <Route path="feedback" element={<FeedbackPage />} />
      <Route path="my-requests" element={<MyRequests />} />
      <Route path="settings" element={<NotificationSettings />} />
      <Route path="folio" element={<Folio />} />
      <Route path="hotel-info" element={<HotelInfo />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

/**
 * Inner app component — rendered INSIDE <AntApp> so hooks like
 * App.useApp() and useNotifications() have access to the Ant context.
 */
const AppInner = () => {
  // Confirm a session restored from localStorage is still live server-side.
  // Without this the app trusts stored state and only discovers an ended
  // session when an unrelated request happens to fail.
  useSessionGuard();

  // Initialize FCM notifications (must be inside AntApp context)
  useNotifications();

  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <Spin size="large" />
        </div>
      }
    >
      <AppRouter />
      <NotificationPrompt />
      <GlobalFeedbackModal />
    </Suspense>
  );
};

const App = () => {
  const themeFromAuth = useSelector(selectThemeFromAuth);
  const themeFromThemeSlice = useSelector(selectAppliedTheme);

  const appliedTheme = themeFromThemeSlice || themeFromAuth || defaultTheme;

  // Update mobile status bar color dynamically
  React.useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', appliedTheme.primaryColor);
    }
  }, [appliedTheme.primaryColor]);

  const themeConfig = {
    token: {
      colorPrimary: appliedTheme.primaryColor,
      fontFamily: appliedTheme.fontStyle || 'Inter, system-ui, -apple-system, sans-serif',
    },
  };

  return (
    <ErrorBoundary>
      <ConfigProvider theme={themeConfig}>
        <AntApp>
          <AppInner />
        </AntApp>
      </ConfigProvider>
    </ErrorBoundary>
  );
};

export default App;
